// Task #6857 — "Trusted by" partner-logo scrolling marquee, extracted from
// ProfessionalPage so it can also render on the public reviews landing pages
// (/uk/reviews etc.). The logo inventory, i18n keys and animation are shared;
// only the surrounding surface color is parameterised so the edge gradient
// fades match the host section's background.
//
// Real production / network / agency partners DRIVESHARE Professional has
// supplied classic cars to. Rendered as B&W wordmarks in a continuously
// scrolling marquee. Each entry should use a SimpleIcons slug verified to
// return 200 from cdn.simpleicons.org. When no valid slug exists, use a
// local monochrome SVG/PNG via imgSrc instead (see BBC, Stan, etc.). To add
// a brand with a slug: confirm
// `curl -I https://cdn.simpleicons.org/<slug>/000000` returns 200.

import { useTranslation } from "react-i18next";
import mrBeastLogo from "@assets/image_1777849255627.png";

type PartnerLogo = { name: string; slug?: string; imgSrc?: string };

export const PARTNER_LOGOS: PartnerLogo[] = [
  { name: "Netflix", slug: "netflix" },
  { name: "HBO Max", slug: "hbomax" },
  { name: "Apple TV+", slug: "appletv" },
  { name: "Paramount+", slug: "paramountplus" },
  { name: "NBC", slug: "nbc" },
  { name: "Channel 4", slug: "channel4" },
  { name: "Sky", slug: "sky" },
  { name: "ITVX", slug: "itvx" },
  { name: "Mr Beast", imgSrc: mrBeastLogo },
  { name: "Showtime", slug: "showtime" },
  { name: "Starz", slug: "starz" },
  { name: "CBS", slug: "cbs" },
  { name: "Fox", slug: "fox" },
  { name: "Sony Pictures", slug: "sony" },
  { name: "Roku", slug: "roku" },
  { name: "MUBI", slug: "mubi" },
  { name: "Foxtel", slug: "foxtel" },
  { name: "Stan", imgSrc: "/images/company/partners/stan.png" },
  { name: "Binge", imgSrc: "/images/company/partners/binge.png" },
  { name: "ABC iview", imgSrc: "/images/company/partners/abc-iview.png" },
  { name: "Screen Australia", imgSrc: "/images/company/partners/screen-australia.svg" },
  { name: "BBC", imgSrc: "/images/company/partners/bbc.png" },
  { name: "Booking.com", slug: "bookingdotcom" },
];

/**
 * The marquee body (heading, scrolling track, caption) WITHOUT a wrapping
 * <section>, so callers can wrap it in their own section (motion.section on
 * the Professional page, a plain section on landing pages).
 *
 * `fadeFromClass` must visually match the host section's background so the
 * left/right edge fades blend (e.g. "from-muted/50" on a bg-muted/50 section).
 */
export function TrustedByMarqueeBody({ fadeFromClass = "from-muted/50" }: { fadeFromClass?: string }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-6xl px-4">
      <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#7a857a]">
        {t("professional.social.trusted_by")}
      </p>
      <div
        className="relative mt-6 overflow-hidden"
        aria-label={t("professional.social.trusted_by")}
        role="region"
      >
        <style>{`
          @keyframes ds-pro-marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .ds-pro-marquee-track { animation: ds-pro-marquee 60s linear infinite; }
          .ds-pro-marquee:hover .ds-pro-marquee-track { animation-play-state: paused; }
          @media (prefers-reduced-motion: reduce) {
            .ds-pro-marquee-track { animation: none; }
          }
        `}</style>
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r ${fadeFromClass} to-transparent sm:w-28`}
        />
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l ${fadeFromClass} to-transparent sm:w-28`}
        />
        <div className="ds-pro-marquee">
          <div className="ds-pro-marquee-track flex w-max items-center gap-x-14 sm:gap-x-20">
            {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((logo, idx) => (
              <div
                key={`${logo.name}-${idx}`}
                className="flex h-20 shrink-0 items-center justify-center"
                title={logo.name}
              >
                <img
                  src={logo.imgSrc ?? `https://cdn.simpleicons.org/${logo.slug}/000000`}
                  alt={logo.name}
                  loading="lazy"
                  className="h-10 w-auto opacity-70 grayscale transition-opacity duration-300 hover:opacity-100 sm:h-12"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-[11px] uppercase tracking-[0.18em] text-[#9aa39a]">
        {t("professional.partners_caption")}
      </p>
    </div>
  );
}

/** Self-contained section variant for pages without their own wrapper. */
export default function TrustedByMarquee({ className = "bg-muted/50 py-14", fadeFromClass = "from-muted/50" }: { className?: string; fadeFromClass?: string }) {
  return (
    <section className={className} data-testid="trusted-by-marquee">
      <TrustedByMarqueeBody fadeFromClass={fadeFromClass} />
    </section>
  );
}
